import React from 'react';
import './TimeInputWithPicker.css';

/**
 * TimeInputWithPicker - Three-dropdown time selector (hour / minute / AM-PM)
 *
 * Pairs with DateInputWithPicker to replace a single datetime-local field.
 * Uses three small native <select>s so there's no long scroll: hour is 12
 * options, minute is 4 (15-min steps by default), AM/PM is 2.
 *
 * Value in/out is always a 24h "HH:MM" string so it pairs cleanly with a
 * date field to reconstruct a "YYYY-MM-DDTHH:MM" datetime-local value.
 *
 * @param {Object} props
 * @param {string} props.id - Base id; the three selects get -hour/-minute/-period suffixes
 * @param {string} props.value - Time value as "HH:MM" (24h) or empty
 * @param {Function} props.onChange - Callback when time changes: (hhmm) => void
 * @param {boolean} props.error - Whether the selects get error styling
 * @param {boolean} props.required - Whether the field is required
 * @param {Object} props.style - Additional styles for the container
 * @param {number} [props.step=15] - Minute increment for the minute dropdown
 */
const TimeInputWithPicker = ({
    id,
    value = '',
    onChange,
    error = false,
    required = false,
    style = {},
    step = 15
}) => {
    const { hour12, minute, period } = parseValue(value);

    // Minute dropdown options on the step grid (0,15,30,45 by default). A value
    // imported from ICS often has an off-grid minute (e.g. 7:50 PM) — include it
    // as an extra option, otherwise the controlled <select> can't display it and
    // renders blank (the pre-populate "fails").
    const minuteOptions = [];
    for (let m = 0; m < 60; m += step) {
        minuteOptions.push(m);
    }
    if (minute != null && !minuteOptions.includes(minute)) {
        minuteOptions.push(minute);
        minuteOptions.sort((a, b) => a - b);
    }

    // Emit "HH:MM" whenever any part changes. If the field is still empty and
    // the user touches one dropdown, default the untouched parts so we always
    // produce a complete, valid time.
    const emit = (next) => {
        const h12 = next.hour12 ?? hour12 ?? 12;
        const min = next.minute ?? minute ?? 0;
        const per = next.period ?? period ?? 'AM';

        let h24 = h12 % 12;            // 12 -> 0
        if (per === 'PM') h24 += 12;   // 0..11 -> 12..23

        onChange(`${String(h24).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
    };

    const selectClass = error ? 'time-select error' : 'time-select';

    return (
        <div className="time-input-with-picker" style={style}>
            <select
                id={id ? `${id}-hour` : undefined}
                className={selectClass}
                value={hour12 ?? ''}
                required={required}
                onChange={e => emit({ hour12: Number(e.target.value) })}
            >
                <option value="" disabled>Hr</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                    <option key={h} value={h}>{h}</option>
                ))}
            </select>

            <span className="time-separator">:</span>

            <select
                id={id ? `${id}-minute` : undefined}
                className={selectClass}
                value={minute ?? ''}
                required={required}
                onChange={e => emit({ minute: Number(e.target.value) })}
            >
                <option value="" disabled>Min</option>
                {minuteOptions.map(m => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                ))}
            </select>

            <select
                id={id ? `${id}-period` : undefined}
                className={selectClass}
                value={period ?? ''}
                required={required}
                onChange={e => emit({ period: e.target.value })}
            >
                <option value="" disabled>AM/PM</option>
                <option value="AM">AM</option>
                <option value="PM">PM</option>
            </select>
        </div>
    );
};

/**
 * Parse a 24h "HH:MM" value into 12h parts.
 * Returns nulls when empty/invalid so the selects show their placeholder.
 */
function parseValue(hhmm) {
    if (!hhmm || typeof hhmm !== 'string') {
        return { hour12: null, minute: null, period: null };
    }
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) {
        return { hour12: null, minute: null, period: null };
    }
    const period = h < 12 ? 'AM' : 'PM';
    let hour12 = h % 12;
    if (hour12 === 0) hour12 = 12;
    return { hour12, minute: m, period };
}

export default TimeInputWithPicker;
